import * as React from "react";
import { Switch as SwitchPrimitive } from "radix-ui";

import { cn } from "./utils";

/**
 * Toggle switch — shadcn-style binary control built on radix-ui's
 * Switch primitive. Use for on/off settings (section enablement, etc.)
 * where the action is reversible and immediate. For confirmation-
 * required actions use a Button with a verb instead.
 *
 * Two densities, matching the control-height scale:
 *   - `default` (h-5/w-9) — settings pages, where the switch is the row's subject.
 *   - `sm` (h-4/w-7) — dense menu panels, where the switch sits in a stack beside
 *     h-6 selects and the default reads as a chunky outlier.
 */
function Switch({
  className,
  size = "default",
  ...props
}: React.ComponentProps<typeof SwitchPrimitive.Root> & {
  size?: "sm" | "default";
}) {
  const sm = size === "sm";
  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      data-size={size}
      className={cn(
        "peer inline-flex shrink-0 cursor-pointer items-center rounded-full border border-transparent outline-none transition-colors",
        sm ? "h-4 w-7" : "h-5 w-9",
        // ON is neutral ink, not a saturated accent: the grayscale substrate
        // reserves color for meaning, so a column of toggles must not read as a
        // column of cobalt. Off = a faint track; on = solid foreground.
        "bg-foreground/15 data-[state=checked]:bg-foreground",
        "focus-visible:ring-2 focus-visible:ring-foreground/30 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(
          "pointer-events-none block rounded-full bg-background shadow-sm transition-transform",
          // Travel = track width − thumb − the 2px inset, so both sizes seat the
          // thumb the same distance from each end.
          sm
            ? "size-3 translate-x-0.5 data-[state=checked]:translate-x-[0.875rem]"
            : "size-4 translate-x-0.5 data-[state=checked]:translate-x-[1.125rem]",
        )}
      />
    </SwitchPrimitive.Root>
  );
}

export { Switch };
